import {
	DeleteOutlined,
	MessageOutlined,
	SaveOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import {
	InputRTE,
	InputRTEProps,
	normalizeRichText,
} from "components/Input/InputRTE"
import { Popover } from "components/Popover/Popover"
import { Tag } from "components/Tag/Tag"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import {
	useProfileNote,
	useUnpinMessage,
	useUpdateProfileNote,
} from "services/patients"
import { cn } from "styles/cn"
import { dateFormatter } from "utils/date"

export const EditProfileNote = ({
	patientId,
	isEditing,
	variant,
	onClose,
}: {
	patientId: string
	isEditing?: boolean
	onClose?: VoidFunction
} & Pick<InputRTEProps, "variant">) => {
	const [value, setValue] = useState<string>()
	const updateNote = useUpdateProfileNote(patientId)
	const { data: note, isFetched } = useProfileNote(patientId)
	const unPin = useUnpinMessage(patientId)
	const pinnedMessages =
		note?.entries.filter(({ references }) =>
			references.some(({ type }) => type === "message"),
		) ?? []
	const defaultValue = useMemo(
		() => normalizeRichText(note?.content ?? ""),
		[note?.content],
	)
	const saveDisabled = defaultValue === value || updateNote.isPending
	const handleSave = useCallback(() => {
		updateNote.mutate(
			{ content: value ?? "" },
			{
				onSuccess: onClose,
			},
		)
	}, [onClose, updateNote, value])

	useHotkeys(
		"mod+enter",
		(e) => {
			e.preventDefault()
			handleSave()
		},
		{
			enabled: Boolean(isEditing && !saveDisabled),
			enableOnFormTags: true,
			enableOnContentEditable: true,
			preventDefault: true,
		},
	)

	useEffect(() => {
		if (note) {
			setValue(defaultValue)
		}
	}, [note, defaultValue])

	if (!isFetched) {
		return null
	}

	const hasContent = Boolean(value?.trim())

	return (
		<fieldset
			disabled={updateNote.isPending}
			className="flex w-full flex-col gap-4"
		>
			{(isEditing || hasContent) && (
				<Form.Item
					className="mb-0"
					help={updateNote.error?.message}
					validateStatus={updateNote.isError ? "error" : undefined}
				>
					<InputRTE
						aria-label="Notes"
						placeholder="Add notes..."
						value={value}
						minRows={2}
						maxRows={4}
						readOnly={!isEditing}
						variant={variant}
						onChange={setValue}
					/>
				</Form.Item>
			)}

			{pinnedMessages.length > 0 && (
				<ul
					className="flex flex-col gap-2"
					aria-label={`${pinnedMessages.length} pinned message(s) available`}
				>
					{pinnedMessages.map(({ id, content, references }, index) => (
						<Popover
							key={id}
							trigger="click"
							placement="bottom"
							classNames={{
								root: "w-full max-w-2xl",
							}}
							title={
								<div className="flex items-center justify-between gap-1">
									<Tag variant="filled">
										{dateFormatter(references[0].timestamp, { hasYear: false })}
									</Tag>
									<Button
										size="small"
										icon={<DeleteOutlined aria-hidden />}
										loading={unPin.isPending}
										onClick={() => unPin.mutate(id)}
									>
										Unpin message
									</Button>
								</div>
							}
							content={<p className="whitespace-pre-wrap">{content}</p>}
						>
							<li
								aria-label={`#${index + 1} message`}
								className="flex w-fit items-center gap-2 rounded-xl bg-mint-0 px-3 py-2 hover:cursor-pointer"
							>
								<div className="flex size-6 place-content-center self-start">
									<MessageOutlined aria-hidden className="text-xl" />
								</div>
								<p className="line-clamp-1 whitespace-pre-wrap">{content}</p>
							</li>
						</Popover>
					))}
					<ErrorStatus show={unPin.isError && !unPin.isPending}>
						{unPin.error?.message}
					</ErrorStatus>
				</ul>
			)}

			<div
				className={cn(
					"hidden items-center justify-end gap-3",
					isEditing && "flex",
				)}
			>
				<Button
					onClick={() => {
						setValue(defaultValue)
						onClose?.()
					}}
				>
					Cancel
				</Button>
				<Button
					type="primary"
					icon={<SaveOutlined aria-hidden />}
					disabled={saveDisabled}
					loading={updateNote.isPending}
					onClick={handleSave}
				>
					Save changes
				</Button>
			</div>
		</fieldset>
	)
}
